/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import React, { useEffect, useMemo, useState } from 'react';

import { TextField as MuiTextField, TextFieldProps as MuiTextFieldProps, formLabelClasses, inputBaseClasses, outlinedInputClasses, styled } from '@mui/material';
import { IFormState, IFieldInputProps, useFormErrorBoundary } from '@ngt/forms-core';
import { IAdditionalFormInputProps } from './Input';
import useErrorStyling, { ErrorClassMapping, ErrorStylingType } from '../../../hooks/form/useErrorStyling';
import { FormError } from '../../../errors';
import FormErrorType from '@/types/form/FormErrorType';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

export const TYPE_PASSWORD = 'password';
export const TYPE_TEXT = 'text';
export const TYPE_EMAIL = 'email';
export const TYPE_NUMBER = 'number';
export const TYPE_URL = 'url';
export const TYPE_TELEPHONE = 'tel';
export const TYPE_DATE = 'date';
export const TYPE_DATETIME_LOCAL = 'datetime-local';
export const TYPE_MONTH = 'month';
export const TYPE_TIME = 'time';
export const TYPE_WEEK = 'week';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

export type TEXT_FIELD_TYPE =
    | typeof TYPE_PASSWORD
    | typeof TYPE_TEXT
    | typeof TYPE_EMAIL
    | typeof TYPE_NUMBER
    | typeof TYPE_URL
    | typeof TYPE_TELEPHONE
    | typeof TYPE_DATE
    | typeof TYPE_DATETIME_LOCAL
    | typeof TYPE_MONTH
    | typeof TYPE_TIME
    | typeof TYPE_WEEK;

type TextFieldsProps = Partial<Omit<MuiTextFieldProps, 'type'>> & IFieldInputProps<string, any, FormError, string> & IAdditionalFormInputProps

export interface ITextProps extends TextFieldsProps {

}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledMuiTextField = styled(MuiTextField)(({theme, margin}) => ({
    marginTop: margin === 'normal' ?
        theme.spacing(2) :
        margin === 'dense' ?
            theme.spacing(1) :
            theme.spacing(0),
    marginBottom: margin === 'normal' ?
        theme.spacing(1) :
        margin === 'dense' ?
            theme.spacing(0.5) :
            theme.spacing(0),
    [`& .${inputBaseClasses.root}`]: {
        padding: theme.spacing(0)
    },
    [`& .${inputBaseClasses.input}`]: {
        padding: theme.spacing(2, 2)
    },
    [`& .${formLabelClasses.root}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(2)}) scale(1)`
    },
    [`& label.${formLabelClasses.focused}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(-1.25)}) scale(1)`
    },
    [`& label.${formLabelClasses.filled}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(-1.25)}) scale(1)`
    },
    [`& fieldset.${outlinedInputClasses.notchedOutline}`]: {
        padding: theme.spacing(0, 1),

        '& legend': {
            fontSize: '1em',
            '& span': {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1)
            }
        }
    }
}))


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const UneditableText: React.FunctionComponent<ITextProps> = ({
    FieldInputProps: { state: { value }, actions: {  update, blur, focus, touch } },
    fullWidth,
    onChange,
    sx,
    inputProps: { onFocus: inputPropsFocus, onBlur: inputPropsBlur, ...inputProps } = {},
    hasErrors,
    errors,
    errorType,
    value: directValue,
    ...rest
}) => {
    const [localValue, setLocalValue] = useState(() => value ?? '');

    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value, setLocalValue])

    const onChangeCombined = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {        
        setLocalValue(event.target.value);

        if (onChange) {
            onChange(event);
        }

        update(event.target.value && event.target.value !== '' ? event.target.value : undefined as any, true, true, true);
    }, [onChange, update, setLocalValue]);

    const onFocusCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsFocus) {
            inputPropsFocus(event);
        }

        focus();
    }, [inputPropsFocus, focus]);

    const onBlurCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsBlur) {
            inputPropsBlur(event);
        }

        blur();
        touch();
    }, [inputPropsBlur, blur, touch]);

    const errorStyling = useErrorStyling(errorType, ErrorStylingType.Input);

    return (
        <StyledMuiTextField
            type="text"
            {...rest as any}
            fullWidth={fullWidth === undefined ? true : fullWidth}
            inputProps={{
                ...inputProps,
                onBlur: onBlurCombined,
                onFocus: onFocusCombined
            }}
            value={directValue ?? localValue}
            onChange={onChangeCombined}
            sx={{...sx, ...errorStyling}}
            error={hasErrors}
            disabled
            helperText={ 
                (hasErrors || rest.helperText) && (
                    <>
                        {rest.helperText && (<span className={ErrorClassMapping[FormErrorType.HelperText]}>{rest.helperText}</span>)} 
                        {errors && errors.length > 0 && errors?.map((error, i) => (<span className={ErrorClassMapping[error.type!]} key={i}>{error.message}</span>))}
                    </>
                )
            }
        />
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default UneditableText;
